/**
 * Context-based hooks for terms data access
 *
 * These hooks provide access to terms data from StudentDataContext.
 * Data fetching is now automatic based on URL parameters - the context
 * observes URL changes and fetches the appropriate data.
 */

import { useStudentData } from "../StudentDataContext";
import type { Term, Week } from "@ts/interfaces/terms/api/termsApiInterface";

/**
 * Hook to access terms state from context
 */
export function useTermsSelector() {
  const {
    terms,
    termsStatus,
    termsError,
    termsForGradeCode,
    isTermsReadyForGrade,
    isWeekLessonsReady,
    selectedTerm,
    selectedWeek,
    currentWeekSubjects,
    weekLessonsStatus,
    weekLessonsError
  } = useStudentData();

  return {
    terms,
    selectedTerm,
    selectedWeek,
    currentWeekSubjects,
    getTermsSummaryRequestStatus: termsStatus,
    getTermsSummaryRequestError: termsError,
    getWeekLessonsRequestStatus: weekLessonsStatus,
    getWeekLessonsRequestError: weekLessonsError,
    // Track which grade the data was fetched for
    forGradeCode: termsForGradeCode,
    isTermsReady: isTermsReadyForGrade,
    isWeekLessonsReady
  };
}

/**
 * Hook to access terms actions from context
 *
 * Note: Data fetching is now automatic based on URL parameters.
 * The context observes gradeId/termId/weekId from the URL and fetches
 * the appropriate terms and week lessons data automatically.
 */
export function useTermsDispatch() {
  const {
    setSelectedTerm,
    setSelectedWeek,
    resetTermsDetails
  } = useStudentData();

  return {
    setSelectedTerm: (term: Term | null) => setSelectedTerm(term),
    setSelectedWeek: (week: Week | null) => setSelectedWeek(week),
    resetTermsDetails: () => resetTermsDetails()
  };
}
